const specialTlds = ['uk', 'au', 'vuk', 'vau'];

// returns something like ["spreadshirt", "co.uk"]
const splitCurrentBaseLocation = () => {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  let tld = parts.pop();

  // check special top level domains; add the second last part to belong to the tld
  if (specialTlds.indexOf(tld) >= 0) {
    tld = [parts.pop(), tld].join('.');
  }

  const sld = parts.pop();
  return [sld, tld];
};

// returns something like spreadshirt.de or vm200.co.vuk
const getBaseHost = () => splitCurrentBaseLocation().join('.');

const isEnv = (env) => SPRD && SPRD.ENVIRONMENT === env;
const customShopTarget = () =>
  SPRD?.SHOP_LOCAL_TARGET ? SPRD.SHOP_LOCAL_TARGET : null;

const livePreviewSuffix = (livePreviewMode, previewToken) => {
  if (!livePreviewMode) return '';
  const params = ['noCache=true', 'liveEdit=true'];
  if (previewToken) {
    params.push(`previewToken=${encodeURIComponent(previewToken)}`);
  }
  return `?${params.join('&')}`;
};

const getStaticSpreadshopUrl = (shopName, path, livePreviewMode) => {
  if (customShopTarget()) {
    // Same local-dev override as the new-storefront path: the local storefront
    // always serves HTTPS (on port 8443), and customShopTarget carries the port
    // (e.g. local.myspreadshop.de:8443).
    const base = `https://${shopName}.${customShopTarget()}`;
    return `${base}/${path}${livePreviewSuffix(livePreviewMode)}`;
  }

  let subDomain;
  if (window.location.hostname.startsWith('rentrap')) {
    subDomain = 'lhc'; // special prerelease environment
  } else {
    subDomain = 'spreadshop-admin';
  }
  const base = `https://${subDomain}.${getBaseHost()}`;
  return `${base}/${shopName}/${path}${livePreviewSuffix(livePreviewMode)}`;
};

const getMyspreadshopUrl = (
  shopName,
  path,
  livePreviewMode = false,
  previewToken,
) => {
  const [sld, tld] = splitCurrentBaseLocation();
  const base = isEnv('QA') ? `${sld}-myspreadshop` : 'myspreadshop';

  return `https://${shopName}.${base}.${tld}/${path}${livePreviewSuffix(livePreviewMode, previewToken)}`;
};

export const getSpreadshopUrl = (shop, path, livePreviewMode, previewToken) => {
  if (!shop || !shop.id) {
    return '';
  }
  const shopName = shop.name || shop.id;

  // New storefront has no separate admin URL. Public and live-preview URLs are the
  // same myspreadshop subdomain; live preview adds liveEdit query params plus a
  // previewToken (when supplied) used to authorize the owner-preview features.
  // customShopTarget remains the local-dev override for running shop-storefront
  // locally; the local storefront always serves HTTPS (on port 8443), so the
  // override host is expected to carry the port, e.g. local.myspreadshop.de:8443.
  if (shop.newStorefront) {
    if (customShopTarget()) {
      return `https://${shopName}.${customShopTarget()}/${path}${livePreviewSuffix(livePreviewMode, previewToken)}`;
    }
    return getMyspreadshopUrl(shopName, path, livePreviewMode, previewToken);
  }

  if (livePreviewMode || window.location.hostname.startsWith('rentrap')) {
    return getStaticSpreadshopUrl(shopName, path, livePreviewMode);
  }
  return getMyspreadshopUrl(shopName, path);
};

export const getSpreadshopFontUrl = (fontDir, fontName) => {
  return `https://spreadshop-admin.${getBaseHost()}/shopfiles/fonts/${fontDir}/${fontName}`;
};

export const getSpreadshopSharingImageUrl = (
  shopId,
  tld, // something like de or co.vuk
  tag,
  ideaId = null,
  sellableId = null,
  appearanceId = null,
) => {
  const baseUrl = `${getMyspreadshopUrl(shopId, shopId)}/shopData/share/${tag}`;
  if (!ideaId) {
    return baseUrl;
  }
  if (!sellableId) {
    return `${baseUrl}?ideaId=${ideaId}`;
  }
  if (!appearanceId) {
    return `${baseUrl}?ideaId=${ideaId}&sellableId=${sellableId}`;
  }
  return `${baseUrl}?ideaId=${ideaId}&sellableId=${sellableId}&appearanceId=${appearanceId}`;
};
